import React, { useRef, useState } from 'react';
import { message } from 'antd';
import { downloadNodeAsPng } from '../../../common/pngExport';

/**
 * A report card surface that can save itself as a PNG, with the button revealed on hover.
 *
 * The button lives on the card element itself rather than in a wrapper, so the DOM is unchanged from
 * the hand-written `div`s these replace — no extra box to perturb the grid tracks or the design's
 * spacing — and the captured node is exactly the card the reader is pointing at.
 *
 * Capture reuses the shared `pngExport` pipeline: html-to-image via an SVG foreignObject, so the browser
 * paints it and the `conic-gradient` donut, the webfonts and every percentage-width bar come out as
 * rendered. `data-download-ignore` keeps the button out of its own capture (pngExport hides those for
 * the duration, preserving their box so nothing reflows).
 */

const SURFACE = 'rounded-ds-lg border border-gray-300 bg-white';

const ReportCard = ({ fileName, className = '', surface = SURFACE, children, ...rest }) => {
  const nodeRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // pngExport pads every export with white, so the card's border is not flush against the image edge.
      await downloadNodeAsPng(nodeRef.current, fileName);
    } catch (error) {
      console.error('PNG export failed:', error);
      message.error('Could not generate the image, please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={nodeRef}
      className={`group/capture relative ${surface} ${className}`.trim()}
      {...rest}
    >
      {/* Top-right inside the card, as a float rather than absolutely positioned. A float displaces the
          line boxes beside it, so a long heading wraps around the button instead of running underneath
          it — which is what absolute positioning did to "How many abnormalities does each employee
          have?". The consequence is that it reserves that space even while invisible; that is the price
          of keeping the control inside the card and out of the text. */}
      <div
        className='pointer-events-none float-right ml-ds-3 -translate-y-1 opacity-0 transition duration-200 ease-out group-hover/capture:pointer-events-auto group-hover/capture:translate-y-0 group-hover/capture:opacity-100 group-focus-within/capture:pointer-events-auto group-focus-within/capture:translate-y-0 group-focus-within/capture:opacity-100'
      >
        <button
          type='button'
          data-download-ignore='true'
          onClick={handleClick}
          aria-label={`Download ${fileName} as PNG`}
          className='inline-flex items-center gap-[5px] rounded-[8px] border border-gray-900 bg-gray-900/5 px-[7px] py-[4px] text-ts-2xs font-medium leading-none text-gray-900 transition-colors duration-150 hover:bg-gray-900/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand focus-visible:ring-offset-1'
        >
          {busy ? (
            <span className='h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-gray-900' />
          ) : (
            <svg
              viewBox='0 0 24 24'
              className='h-3 w-3'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M12 3v12' />
              <path d='m7 11 5 4 5-4' />
              <path d='M5 21h14' />
            </svg>
          )}
          PNG
        </button>
      </div>
      {children}
    </div>
  );
};

export default ReportCard;
